/**
 * The cards on an account, and the switch that charges one.
 *
 * Opened from the Autopay tile on the member record, which is what
 * Shaun asked for: "autopay (should pull up payment method CC entry)".
 *
 * ---------------------------------------------------------------------
 * WHAT THIS SCREEN DOES NOT DO
 *
 * It does not take a card number. Not in a hidden field, not "just for
 * a moment before we tokenize it" — the box that would hold one does not
 * exist, and the API refuses a value shaped like a card number even if
 * somebody builds one.
 *
 * What it takes is the REFERENCE the club's terminal or processor gives
 * back, which is what `payment_method.processor_token` has always held.
 * On a club running its own terminal that is the string printed on the
 * merchant slip; on a club with a hosted processor it is what the
 * processor's own field hands over. The rest of this screen — brand, four
 * digits, expiry — is what staff read off the card in front of them so
 * the member can later be told which card it was.
 *
 * That is the honest shape of the thing until a processor is chosen, and
 * it is a shape that works: a club running a Verifone terminal behind its
 * own merchant account is a real deployment and the one RCC starts on.
 *
 * ---------------------------------------------------------------------
 * TWO AUTOPAYS, WHICH IS THE BUG WAITING TO HAPPEN
 *
 * `member_account.autopay_enabled` is what the nightly run reads.
 * `payment_method.is_autopay` is WHICH card it takes. A club can set
 * either and believe it has set both, and the failure is silent: the run
 * finds an account marked for autopay with no card, or a card marked for
 * autopay on an account that never runs. So this screen shows them as
 * one thing and says so when they disagree.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status } from './ui.tsx';

interface Method {
  id: string;
  method_type: 'card' | 'ach' | 'other';
  display_brand: string | null;
  display_last4: string | null;
  expires_month: number | null;
  expires_year: number | null;
  is_default: boolean;
  is_autopay: boolean;
  status: 'active' | 'expired' | 'removed';
}

/** Expired, or close enough that somebody should be told now. */
function expiry(m: Method): { label: string; tone: 'good' | 'warning' | 'critical' } | null {
  if (!m.expires_month || !m.expires_year) return null;
  const now = new Date();
  // A card is good through the last day of its expiry month, which is
  // the thing people get wrong by a month in both directions.
  const endOfMonth = new Date(m.expires_year, m.expires_month, 0);
  const label = `${String(m.expires_month).padStart(2, '0')}/${String(m.expires_year).slice(2)}`;
  if (endOfMonth < now) return { label: `${label} — expired`, tone: 'critical' };
  const soon = new Date();
  soon.setMonth(soon.getMonth() + 2);
  if (endOfMonth < soon) return { label: `${label} — expiring`, tone: 'warning' };
  return { label, tone: 'good' };
}

export function PaymentMethods({
  accountId,
  autopayEnabled,
  onChanged,
}: {
  accountId: string;
  /** The account's own switch — what the nightly run reads. */
  autopayEnabled: boolean;
  onChanged: () => void;
}) {
  const { atLeast } = useSession();
  const canEdit = atLeast('manager');
  const [adding, setAdding] = useState(false);

  const list = useApi<{ payment_methods: Method[] }>(
    `/v1/member-accounts/${accountId}/payment-methods`,
    [accountId],
  );

  const update = useMutation(async (id: string, body: Record<string, unknown>) =>
    api(`/v1/payment-methods/${id}`, { method: 'PATCH', body }),
  );
  const setAccountAutopay = useMutation(async (on: boolean) =>
    api(`/v1/members/${accountId}`, { method: 'PATCH', body: { autopay_enabled: on } }),
  );

  const methods = list.data?.payment_methods ?? [];
  const autopayCard = methods.find((m) => m.is_autopay && m.status === 'active');

  const refresh = () => {
    list.reload();
    onChanged();
  };

  return (
    <Card
      title="Payment Methods"
      actions={
        canEdit && !adding ? (
          <button className="secondary small" onClick={() => setAdding(true)}>
            Add a card
          </button>
        ) : null
      }
    >
      <ErrorBanner error={list.error ?? update.error ?? setAccountAutopay.error} />

      {/*
        The two autopays, reconciled out loud.
        Each of these disagreements is silent in the database and costs a
        club a month of uncollected dues before anybody notices.
      */}
      {autopayEnabled && !autopayCard && (
        <div className="error-banner">
          <strong>Autopay is on and there is no card to charge.</strong>
          <div style={{ marginTop: 4 }}>
            The nightly run will find nothing on this account. Add a card and mark it for
            autopay, or turn autopay off.
          </div>
        </div>
      )}
      {!autopayEnabled && autopayCard && (
        <div className="error-banner">
          <strong>A card is marked for autopay and autopay is off.</strong>
          <div style={{ marginTop: 4 }}>
            Nothing will be charged to it.{' '}
            {canEdit && (
              <button
                className="linkish"
                onClick={async () => {
                  const done = await setAccountAutopay.run(true);
                  if (done) refresh();
                }}
              >
                Turn autopay on
              </button>
            )}
          </div>
        </div>
      )}

      {adding && (
        <AddCard
          accountId={accountId}
          isFirst={methods.length === 0}
          onDone={() => {
            setAdding(false);
            refresh();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {list.loading ? (
        <Loading rows={2} />
      ) : methods.length === 0 ? (
        <Empty>
          No card on file. Dues and statements are settled by check or at the desk until there
          is one.
        </Empty>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Card</th>
              <th style={{ width: 150 }}>Expires</th>
              <th style={{ width: 170 }}>Role</th>
              {canEdit && <th style={{ width: 250 }} />}
            </tr>
          </thead>
          <tbody>
            {methods.map((m) => {
              const e = expiry(m);
              return (
                <tr key={m.id} style={m.status !== 'active' ? { opacity: 0.55 } : undefined}>
                  <td>
                    <strong>{m.display_brand ?? m.method_type.toUpperCase()}</strong>
                    {m.display_last4 && (
                      <span className="mono muted"> ···· {m.display_last4}</span>
                    )}
                  </td>
                  <td>
                    {e ? (
                      e.tone === 'good' ? (
                        <span className="mono">{e.label}</span>
                      ) : (
                        <Status tone={e.tone}>{e.label}</Status>
                      )
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    {m.is_autopay && <Status tone="good">Autopay</Status>}
                    {m.is_default && !m.is_autopay && (
                      <span className="muted small">Default</span>
                    )}
                    {!m.is_default && !m.is_autopay && <span className="muted">—</span>}
                    {m.status !== 'active' && (
                      <div className="muted small">{m.status}</div>
                    )}
                  </td>
                  {canEdit && (
                    <td className="actions">
                      {m.status === 'active' && !m.is_autopay && (
                        <button
                          className="secondary small"
                          disabled={update.busy}
                          onClick={async () => {
                            // Both switches at once. Marking a card for
                            // autopay while the account's own switch is
                            // off is the disagreement above, and making
                            // somebody fix it in two places is how it
                            // gets left half done.
                            const done = await update.run(m.id, {
                              is_autopay: true,
                              is_default: true,
                            });
                            if (done) {
                              await setAccountAutopay.run(true);
                              refresh();
                            }
                          }}
                        >
                          Use for autopay
                        </button>
                      )}
                      {m.status === 'active' && m.is_autopay && (
                        <button
                          className="secondary small"
                          disabled={update.busy}
                          onClick={async () => {
                            const done = await update.run(m.id, { is_autopay: false });
                            if (done) refresh();
                          }}
                        >
                          Stop autopay
                        </button>
                      )}
                      {m.status === 'active' && (
                        <button
                          className="secondary small"
                          disabled={update.busy}
                          onClick={async () => {
                            const done = await update.run(m.id, { status: 'removed' });
                            if (done) refresh();
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="hint" style={{ marginTop: 10 }}>
        A card number is never stored, never logged and never reaches this application. What is
        kept is the reference the terminal or the processor gives back, the brand, the last four
        digits and the expiry — enough to tell a member which card was charged, and not enough to
        charge it anywhere else.
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */

function AddCard({
  accountId,
  isFirst,
  onDone,
  onCancel,
}: {
  accountId: string;
  isFirst: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const thisYear = new Date().getFullYear();
  const [f, setF] = useState({
    processor_token: '',
    display_brand: 'Visa',
    display_last4: '',
    expires_month: '',
    expires_year: '',
    // The first card on an account is the default and the autopay card
    // unless somebody says otherwise. A club adding a card is a club
    // that wants it used.
    is_autopay: isFirst,
  });

  const add = useMutation(async () =>
    api(`/v1/member-accounts/${accountId}/payment-methods`, {
      method: 'POST',
      body: {
        method_type: 'card',
        processor: 'manual',
        processor_token: f.processor_token.trim(),
        display_brand: f.display_brand.trim() || null,
        display_last4: f.display_last4.trim() || null,
        expires_month: f.expires_month ? Number(f.expires_month) : null,
        expires_year: f.expires_year ? Number(f.expires_year) : null,
        is_default: true,
        is_autopay: f.is_autopay,
      },
    }),
  );

  const set = (k: keyof typeof f) => (v: string) => setF({ ...f, [k]: v });

  return (
    <div className="card add-card">
      <ErrorBanner error={add.error} />

      <div className="hint" style={{ marginBottom: 10 }}>
        Take the card on the club's own terminal, then record what it gave back. The reference is
        what lets the club charge the card again; nothing here is the card number, and a value
        that looks like one is refused.
      </div>

      <div className="grid-2">
        <label className="field">
          <span>Reference from the terminal</span>
          <input
            value={f.processor_token}
            autoFocus
            autoComplete="off"
            placeholder="The token or reference on the merchant slip"
            onChange={(e) => set('processor_token')(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Card</span>
          <select value={f.display_brand} onChange={(e) => set('display_brand')(e.target.value)}>
            <option>Visa</option>
            <option>Mastercard</option>
            <option>American Express</option>
            <option>Discover</option>
            <option>Other</option>
          </select>
        </label>
        <label className="field">
          <span>Last four digits</span>
          <input
            value={f.display_last4}
            inputMode="numeric"
            maxLength={4}
            placeholder="4242"
            onChange={(e) => set('display_last4')(e.target.value.replace(/\D/g, ''))}
          />
        </label>
        <label className="field">
          <span>Expires</span>
          <div className="row" style={{ gap: 6 }}>
            <select value={f.expires_month} onChange={(e) => set('expires_month')(e.target.value)}>
              <option value="">Month</option>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {String(i + 1).padStart(2, '0')}
                </option>
              ))}
            </select>
            <select value={f.expires_year} onChange={(e) => set('expires_year')(e.target.value)}>
              <option value="">Year</option>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i} value={thisYear + i}>
                  {thisYear + i}
                </option>
              ))}
            </select>
          </div>
        </label>
      </div>

      <label className="muted small" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="checkbox"
          checked={f.is_autopay}
          onChange={(e) => setF({ ...f, is_autopay: e.target.checked })}
        />
        Charge dues and statements to this card automatically
      </label>

      <div className="actions" style={{ marginTop: 12 }}>
        <div className="spacer" />
        <button className="secondary" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="gold"
          disabled={add.busy || f.processor_token.trim().length < 4}
          onClick={async () => {
            const done = await add.run();
            if (done) onDone();
          }}
        >
          {add.busy ? 'Saving…' : 'Save the card'}
        </button>
      </div>
    </div>
  );
}
